import React, { useState } from 'react';
import { format } from 'date-fns';
import JobFormModal from './JobFormModal';

const JobTable = ({ jobs, loading, onUpdate, onDelete, fetchJobs, onViewApplicants }) => {
  const [selectedJob, setSelectedJob] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const handleUpdateClick = (job) => {
    setSelectedJob(job);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedJob(null);
  };

  const handleDeleteClick = (jobId) => {
    onDelete(jobId);
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
    } catch (error) {
      return dateString;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'INACTIVE':
        return 'bg-yellow-100 text-yellow-800';
      case 'CLOSED':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="p-8 text-center">
          <p className="text-gray-500 text-lg">No jobs found.</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Desktop Table View */}
      <div className="hidden md:block bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Job Title
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Category / Subcategory
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Location & Type
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Company
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Applicants
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Created Date
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {jobs.map((job) => (
              <tr key={job.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{job.title}</div>
                  <div className="text-sm text-gray-500 truncate max-w-xs" title={job.description}>
                    {job.description}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{job.category}</div>
                  <div className="text-sm text-gray-500">{job.subcategory}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{job.location || 'Remote'}</div>
                  <div className="text-sm text-gray-500">{job.jobType || 'Not specified'}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(job.status)}`}>
                    {job.status}
                  </span>
                  <div className="text-xs text-gray-500 mt-1">
                    {job.isActive ? 'Active' : 'Inactive'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{job.companyName || 'Unknown'}</div>
                  <div className="text-sm text-gray-500">{job.companyEmail || ''}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{job.participants || 0} applicants</div>
                  {job.participants > 0 && (
                    <button
                      onClick={() => onViewApplicants && onViewApplicants(job)}
                      className="text-sm text-blue-600 hover:text-blue-900"
                    >
                      View Applicants
                    </button>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{formatDate(job.createdAt)}</div>
                  <div className="text-sm text-gray-500">Updated: {formatDate(job.updatedAt)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end space-x-2">
                    <button
                      onClick={() => handleUpdateClick(job)}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      Update
                    </button>
                    <button
                      onClick={() => handleDeleteClick(job.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {jobs.map((job) => (
          <div key={job.id} className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4">
              <div className="flex justify-between items-start">
                <h3 className="text-lg font-medium text-gray-900">{job.title}</h3>
                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(job.status)}`}>
                  {job.status}
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-500 line-clamp-2">{job.description}</p>
              
              <div className="mt-4 grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="font-medium text-gray-500">Category:</span>
                  <p className="text-gray-900">{job.category}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Subcategory:</span>
                  <p className="text-gray-900">{job.subcategory}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Location:</span>
                  <p className="text-gray-900">{job.location || 'Remote'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Job Type:</span>
                  <p className="text-gray-900">{job.jobType || 'Not specified'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Company:</span>
                  <p className="text-gray-900">{job.companyName || 'Unknown'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Created:</span>
                  <p className="text-gray-900">{formatDate(job.createdAt)}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Active:</span>
                  <p className="text-gray-900">{job.isActive ? 'Yes' : 'No'}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Applicants:</span>
                  <p className="text-gray-900">{job.participants || 0}</p>
                </div>
              </div>
              
              <div className="mt-4 flex justify-end space-x-2">
                {job.participants > 0 && (
                  <button
                    onClick={() => onViewApplicants && onViewApplicants(job)}
                    className="text-blue-600 hover:text-blue-900 font-medium"
                  >
                    View Applicants
                  </button>
                )}
                <button
                  onClick={() => handleUpdateClick(job)}
                  className="text-indigo-600 hover:text-indigo-900 font-medium"
                >
                  Update
                </button>
                <button
                  onClick={() => handleDeleteClick(job.id)}
                  className="text-red-600 hover:text-red-900 font-medium"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Update Modal */}
      {showModal && selectedJob && (
        <JobFormModal
          job={selectedJob}
          onClose={handleCloseModal}
          onSave={(jobData) => {
            onUpdate(selectedJob.id, jobData);
            handleCloseModal();
          }}
        />
      )}
    </>
  );
};

export default JobTable; 